import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import { HorizontalBar, Bar } from 'react-chartjs-2';
import { Page } from 'components';
import Header from 'components/Header/Header';
import {
  AnalyticsDashboardError,
  buildAnalyticsQuery,
  fetchAnalyticsDashboard,
} from 'services/analyticsDashboardApi';
import { KPI, getChartTextColor } from 'constants/dashboardColors';
import { useAppTheme } from '../../contexts/ThemeContext';
import dateFormat from 'utils/dateformat';
import ApiMServer from 'services/ApiMServer';
import './AnalyticsDashboard.css';

const APPLICATION_OPTIONS = [
  { value: 'SGU WEB', label: 'SGU WEB' },
  { value: 'monitora', label: 'Monitora' },
];

const PRESET_DAYS = { '7d': 7, '30d': 30 };

const emptySection = () => ({ data: null, loading: false, error: null });

const isReadEnabled = (permissionEntry) => {
  const read = permissionEntry?.read;
  return read === true || read === 'true' || read === 1;
};

const canAccessAnalytics = (user) => isReadEnabled(user?.permission?.analytics);

const getPresetRange = (preset) => {
  const days = PRESET_DAYS[preset] || 7;
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - days);
  from.setHours(0, 0, 0, 0);
  return { from, to };
};

const formatNumber = (n) => Number(n || 0).toLocaleString('pt-BR');

const truncate = (text, max = 48) => {
  const s = String(text || '');
  return s.length > max ? `${s.slice(0, max)}…` : s;
};

const AnalyticsDashboard = () => {
  const [user, setUser] = useState(() =>
    JSON.parse(localStorage.getItem('userData') || 'null'),
  );
  const darkMode = useAppTheme() === 'dark';

  const [permissionsSynced, setPermissionsSynced] = useState(false);

  useEffect(() => {
    const refreshUser = async () => {
      const stored = localStorage.getItem('userData');
      if (!stored) {
        setPermissionsSynced(true);
        return;
      }
      try {
        const parsed = JSON.parse(stored);
        const { data: newUserData } = await ApiMServer.get(`/users/${parsed._id}`);
        if (newUserData) {
          const updatedUser = { ...parsed, ...newUserData, theme: parsed.theme || 'dark' };
          localStorage.setItem('userData', JSON.stringify(updatedUser));
          setUser(updatedUser);
        }
      } catch (error) {
        console.error('Erro ao atualizar permissões do usuário:', error);
      } finally {
        setPermissionsSynced(true);
      }
    };
    refreshUser();
  }, []);

  const [applicationId, setApplicationId] = useState('SGU WEB');
  const [preset, setPreset] = useState('7d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [forbidden, setForbidden] = useState(false);
  const [globalLoading, setGlobalLoading] = useState(false);

  const [summary, setSummary] = useState(emptySection);
  const [pages, setPages] = useState(emptySection);
  const [equipment, setEquipment] = useState(emptySection);
  const [errors, setErrors] = useState(emptySection);

  useEffect(() => {
    if (!permissionsSynced) return;
    if (!canAccessAnalytics(user)) {
      window.location.replace('/home');
    }
  }, [user, permissionsSynced]);

  const dateRange = useMemo(() => {
    if (preset === 'custom') {
      const from = customFrom ? new Date(`${customFrom}T00:00:00`) : getPresetRange('7d').from;
      const to = customTo ? new Date(`${customTo}T23:59:59`) : new Date();
      return { from, to };
    }
    return getPresetRange(preset);
  }, [preset, customFrom, customTo]);

  const loadDashboard = useCallback(async () => {
    setForbidden(false);
    setGlobalLoading(true);
    setSummary({ ...emptySection(), loading: true });
    setPages({ ...emptySection(), loading: true });
    setEquipment({ ...emptySection(), loading: true });
    setErrors({ ...emptySection(), loading: true });

    const query = buildAnalyticsQuery({
      applicationId,
      from: dateRange.from,
      to: dateRange.to,
    });

    const applyResult = (result, setter) => {
      if (result.status === 'fulfilled') {
        setter({ data: result.value, loading: false, error: null });
        return;
      }
      const err = result.reason;
      const status = err instanceof AnalyticsDashboardError ? err.status : 0;
      if (status === 403) {
        setForbidden(true);
      }
      setter({
        data: null,
        loading: false,
        error: err?.message || 'Falha ao carregar esta seção.',
      });
    };

    const results = await Promise.allSettled([
      fetchAnalyticsDashboard('/analytics/dashboard/summary', query),
      fetchAnalyticsDashboard('/analytics/dashboard/pages', { ...query, limit: 20 }),
      fetchAnalyticsDashboard('/analytics/dashboard/equipment', { ...query, limit: 20 }),
      fetchAnalyticsDashboard('/analytics/dashboard/errors', { ...query, limit: 20 }),
    ]);

    applyResult(results[0], setSummary);
    applyResult(results[1], setPages);
    applyResult(results[2], setEquipment);
    applyResult(results[3], setErrors);
    setGlobalLoading(false);
  }, [applicationId, dateRange.from, dateRange.to]);

  useEffect(() => {
    if (canAccessAnalytics(user)) {
      loadDashboard();
    }
  }, [loadDashboard, user]);

  const chartText = getChartTextColor(darkMode);

  const pagesChart = useMemo(() => {
    const list = (pages.data?.pages || []).slice(0, 12);
    return {
      labels: list.map((p) => truncate(p.page, 36)),
      datasets: [{
        label: 'Visualizações',
        data: list.map((p) => p.views),
        backgroundColor: KPI.primary,
      }],
    };
  }, [pages.data]);

  const equipmentChart = useMemo(() => {
    const list = (equipment.data?.equipment || []).slice(0, 10);
    return {
      labels: list.map((e) => e.equipmentId),
      datasets: [{
        label: 'Total',
        data: list.map((e) => e.total),
        backgroundColor: KPI.secondary,
      }],
    };
  }, [equipment.data]);

  const errorsByEvent = useMemo(() => {
    const list = errors.data?.errors || [];
    const map = {};
    list.forEach((item) => {
      map[item.event] = (map[item.event] || 0) + Number(item.count || 0);
    });
    return {
      labels: Object.keys(map),
      datasets: [{
        label: 'Ocorrências',
        data: Object.values(map),
        backgroundColor: KPI.danger,
      }],
    };
  }, [errors.data]);

  const barOptions = {
    legend: { display: false },
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      yAxes: [{ ticks: { fontColor: chartText, beginAtZero: true }, gridLines: { display: false } }],
      xAxes: [{ ticks: { fontColor: chartText, beginAtZero: true, precision: 0 } }],
    },
  };

  const horizontalOptions = {
    ...barOptions,
    scales: {
      yAxes: [{ ticks: { fontColor: chartText }, gridLines: { display: false }, maxBarThickness: 28 }],
      xAxes: [{ ticks: { fontColor: chartText, beginAtZero: true, precision: 0 } }],
    },
  };

  if (forbidden) {
    return (
      <Page className="p-3" title="Analytics">
        <Header page="Analytics" />
        <Alert variant="warning">
          Sem permissão para acessar o dashboard de analytics no servidor de monitoramento.
          Solicite acesso de administrador ou configure <code>ANALYTICS_DASHBOARD_OPEN</code> em homologação.
        </Alert>
      </Page>
    );
  }

  const summaryData = summary.data || {};

  return (
    <Page className="p-3" title="Analytics">
      <Header page="Motomco Analytics" />

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={3}>
              <Form.Label>Sistema</Form.Label>
              <Form.Select value={applicationId} onChange={(e) => setApplicationId(e.target.value)}>
                {APPLICATION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label>Período</Form.Label>
              <div className="d-flex flex-wrap gap-2">
                {['7d', '30d', 'custom'].map((key) => (
                  <Button
                    key={key}
                    size="sm"
                    variant={preset === key ? 'success' : 'outline-secondary'}
                    onClick={() => setPreset(key)}
                  >
                    {key === '7d' && '7 dias'}
                    {key === '30d' && '30 dias'}
                    {key === 'custom' && 'Personalizado'}
                  </Button>
                ))}
              </div>
            </Col>
            {preset === 'custom' && (
              <>
                <Col md={2}>
                  <Form.Label>De</Form.Label>
                  <Form.Control type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
                </Col>
                <Col md={2}>
                  <Form.Label>Até</Form.Label>
                  <Form.Control type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
                </Col>
              </>
            )}
            <Col md={preset === 'custom' ? 12 : 3} className="text-md-end">
              <Button variant="success" onClick={loadDashboard} disabled={globalLoading}>
                {globalLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Atualizando…
                  </>
                ) : (
                  'Atualizar'
                )}
              </Button>
            </Col>
          </Row>
          <div className="text-muted small mt-2">
            {dateFormat(dateRange.from, 'dd/mm/yy HH:MM')}
            {' — '}
            {dateFormat(dateRange.to, 'dd/mm/yy HH:MM')}
          </div>
        </Card.Body>
      </Card>

      <Row className="g-3 mb-3">
        {[
          { label: 'Usuários ativos', value: summaryData.activeUsers, color: KPI.primary },
          { label: 'Sessões', value: summaryData.sessions, color: KPI.secondary },
          { label: 'Eventos', value: summaryData.totalEvents, color: KPI.tertiary },
          { label: 'Page views', value: summaryData.pageViews, color: KPI.quaternary },
        ].map((kpi) => (
          <Col key={kpi.label} xs={6} lg={3}>
            <Card className="h-100">
              <Card.Body>
                <div className="text-muted small">{kpi.label}</div>
                {summary.loading ? (
                  <Spinner animation="border" size="sm" className="mt-2" />
                ) : (
                  <div className="analytics-dashboard__kpi-value" style={{ color: kpi.color }}>
                    {formatNumber(kpi.value)}
                  </div>
                )}
                {summary.error && !summary.loading && (
                  <Badge bg="danger" className="mt-1">Erro</Badge>
                )}
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Row className="g-3 mb-3">
        <Col lg={6}>
          <Card className="h-100">
            <Card.Header>Páginas mais acessadas</Card.Header>
            <Card.Body>
              {pages.error && <Alert variant="danger" className="py-2">{pages.error}</Alert>}
              {pages.loading && <div className="text-center py-5"><Spinner animation="border" /></div>}
              {!pages.loading && !pages.error && (pages.data?.pages?.length || 0) === 0 && (
                <p className="text-muted mb-0">Nenhum dado no período.</p>
              )}
              {!pages.loading && (pages.data?.pages?.length || 0) > 0 && (
                <div className="analytics-dashboard__chart-wrap">
                  <HorizontalBar data={pagesChart} options={horizontalOptions} />
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6}>
          <Card className="h-100">
            <Card.Header>Equipamentos mais vistos</Card.Header>
            <Card.Body>
              {equipment.error && <Alert variant="danger" className="py-2">{equipment.error}</Alert>}
              {equipment.loading && <div className="text-center py-5"><Spinner animation="border" /></div>}
              {!equipment.loading && !equipment.error && (equipment.data?.equipment?.length || 0) === 0 && (
                <p className="text-muted mb-0">Nenhum dado no período.</p>
              )}
              {!equipment.loading && (equipment.data?.equipment?.length || 0) > 0 && (
                <div className="analytics-dashboard__chart-wrap">
                  <Bar data={equipmentChart} options={barOptions} />
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card>
        <Card.Header>Erros (front e API)</Card.Header>
        <Card.Body>
          {errors.error && <Alert variant="danger" className="py-2">{errors.error}</Alert>}
          {errors.loading && <div className="text-center py-4"><Spinner animation="border" /></div>}
          {!errors.loading && !errors.error && (errors.data?.errors?.length || 0) === 0 && (
            <p className="text-muted mb-0">Nenhum erro registrado no período.</p>
          )}
          {!errors.loading && (errors.data?.errors?.length || 0) > 0 && (
            <>
              {(errorsByEvent.labels?.length || 0) > 0 && (
                <div className="analytics-dashboard__chart-wrap mb-4" style={{ height: 220 }}>
                  <Bar data={errorsByEvent} options={barOptions} />
                </div>
              )}
              <Table responsive hover size="sm">
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Mensagem</th>
                    <th>URL</th>
                    <th className="text-end">Qtd</th>
                    <th>Última</th>
                  </tr>
                </thead>
                <tbody>
                  {(errors.data?.errors || []).map((row, idx) => (
                    <tr key={`${row.event}-${row.message}-${idx}`}>
                      <td><Badge bg="secondary">{row.event}</Badge></td>
                      <td title={row.message}>{truncate(row.message, 64)}</td>
                      <td className="analytics-dashboard__error-url" title={row.url}>{truncate(row.url, 56)}</td>
                      <td className="text-end">{formatNumber(row.count)}</td>
                      <td>
                        {row.lastAt
                          ? dateFormat(row.lastAt, 'dd/mm/yy HH:MM')
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Card.Body>
      </Card>
    </Page>
  );
};

export default AnalyticsDashboard;
